import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WaitlistCTA from "@/components/landing/WaitlistCTA";
import useReveal from "@/hooks/useReveal";
import useSEO from "@/hooks/useSEO";

const contactEmail = "workwithdilip1@gmail.com";

const promises = [
    {
        title: "Hidden by default",
        copy: "Your legal name is never the headline. We reveal identity details only when they are useful for trust and consent.",
    },
    {
        title: "No resale economy",
        copy: "Your dating history, preferences, photos, and messages are not inventory. We do not sell personal data.",
    },
    {
        title: "Real deletion",
        copy: "Account deletion is designed to remove profile data, matching inputs, and media from active systems without a retention maze.",
    },
    {
        title: "Exportable memory",
        copy: "You should be able to see what the system knows about you and take a copy with you.",
    },
];

const controls = ["Photo verification", "Consent-based reveal", "GDPR export", "Delete account", "Block and report", "Private matching notes"];

const dataCategories = [
    ["Account and waitlist data", "Name or chosen display name, email address, city or region, signup source, and alpha invite status."],
    ["Profile and matching data", "Photos, prompts, preferences, relationship intent, interests, compatibility quiz answers, and interaction signals used to suggest matches."],
    ["Safety and verification data", "Photo verification checks, trust reports, moderation notes, block/report records, and abuse-prevention signals."],
    ["Technical data", "Device, browser, approximate location derived from IP, log data, diagnostics, and security events needed to operate and protect the service."],
    ["Communications", "Messages you send to support, form submissions, and service emails related to waitlist or account activity."],
];

const useCases = [
    "Create and maintain accounts, waitlist entries, and alpha invites.",
    "Generate curated matches and compatibility explanations.",
    "Detect fake profiles, spam, harassment, fraud, and unsafe behavior.",
    "Respond to support, privacy, safety, and legal requests.",
    "Improve the product using aggregate analytics and reliability signals.",
];

export default function PrivacyPolicy() {
    useReveal();
    useSEO({
        title: "Privacy Policy",
        description: "How LÜVYN protects your data and preserves your vulnerability.",
        canonical: "/privacy",
    });

    return (
        <div className="relative min-h-screen overflow-x-hidden" data-testid="privacy-page">
            <div className="grain" />
            <Header />
            <main id="top">
                <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[var(--ink)] text-[var(--paper)]">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                        <div className="reveal in font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--hot)] mb-8">
                            Privacy edition · trust department
                        </div>
                        <div className="reveal in grid lg:grid-cols-[1.2fr_0.8fr] gap-12 items-end">
                            <h1 className="font-display text-[17vw] sm:text-[12vw] lg:text-[10rem] leading-[0.82] tracking-tighter">
                                Your story is{" "}
                                <span className="font-italic-serif text-[var(--hot)]">
                                    not
                                </span>
                                <br />
                                for sale.
                            </h1>
                            <p className="text-lg leading-relaxed text-[var(--paper-2)] max-w-xl">
                                Dating requires vulnerability. Our privacy model is
                                built around minimizing exposure, preventing resale,
                                and making control obvious.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="relative py-24 lg:py-32 bg-[var(--paper)]">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                        <div className="reveal grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                            {promises.map((promise, index) => (
                                <article
                                    key={promise.title}
                                    className="border-2 border-[var(--ink)] bg-[var(--paper-2)] p-7 min-h-[280px]"
                                >
                                    <div className="font-display text-5xl text-[var(--hot)]">
                                        {String(index + 1).padStart(2, "0")}
                                    </div>
                                    <h2 className="mt-10 font-display text-3xl leading-tight">
                                        {promise.title}
                                    </h2>
                                    <p className="mt-4 text-sm leading-relaxed text-[var(--ink-soft)]">
                                        {promise.copy}
                                    </p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="relative py-24 lg:py-32 bg-[var(--paper-2)] border-y-2 border-[var(--ink)]">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                        <div className="reveal grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-start">
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-4">
                                    Controls
                                </div>
                                <h2 className="font-display text-6xl lg:text-8xl leading-[0.86] tracking-tighter">
                                    Built-in ways to{" "}
                                    <span className="font-italic-serif text-[var(--hot)]">
                                        say no.
                                    </span>
                                </h2>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                {controls.map((control) => (
                                    <div
                                        key={control}
                                        className="bg-[var(--paper)] border-2 border-[var(--ink)] p-6 font-display text-3xl leading-tight"
                                    >
                                        {control}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="relative py-24 lg:py-32 bg-[var(--paper)]">
                    <div className="max-w-[1300px] mx-auto px-6 lg:px-10">
                        <div className="reveal flex items-end justify-between flex-wrap gap-6 mb-12">
                            <div>
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-4">
                                    Data we may handle
                                </div>
                                <h2 className="font-display text-6xl lg:text-8xl leading-[0.86] tracking-tighter">
                                    The private{" "}
                                    <span className="font-italic-serif text-[var(--hot)]">
                                        ledger.
                                    </span>
                                </h2>
                            </div>
                            <p className="max-w-md leading-relaxed text-[var(--ink-soft)]">
                                Dating data is sensitive. These categories explain
                                what a real LÜVYN app may need to collect and why.
                            </p>
                        </div>

                        <div className="reveal border-y-2 border-[var(--ink)]">
                            {dataCategories.map(([title, copy]) => (
                                <article
                                    key={title}
                                    className="grid lg:grid-cols-[0.45fr_1fr] gap-5 py-7 border-b-2 border-[var(--ink)] last:border-b-0"
                                >
                                    <h3 className="font-display text-3xl lg:text-4xl leading-tight">
                                        {title}
                                    </h3>
                                    <p className="leading-relaxed text-[var(--ink-soft)]">
                                        {copy}
                                    </p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="relative py-24 lg:py-32 bg-[var(--paper-2)] border-y-2 border-[var(--ink)]">
                    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[0.9fr_1.1fr] gap-12">
                        <div className="reveal">
                            <div className="stamp mb-6">Processing rules</div>
                            <h2 className="font-display text-6xl lg:text-8xl leading-[0.86] tracking-tighter">
                                What we use it{" "}
                                <span className="font-italic-serif text-[var(--hot)]">
                                    for.
                                </span>
                            </h2>
                        </div>
                        <div className="reveal bg-[var(--paper)] border-2 border-[var(--ink)] p-7 lg:p-10">
                            <ul className="space-y-5">
                                {useCases.map((item) => (
                                    <li
                                        key={item}
                                        className="flex gap-4 border-b border-[var(--ink)] pb-5 last:border-b-0 last:pb-0"
                                    >
                                        <span className="text-[var(--hot)] font-display text-3xl leading-none">
                                            +
                                        </span>
                                        <span className="leading-relaxed text-[var(--ink-soft)]">
                                            {item}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-8 text-sm leading-relaxed text-[var(--ink-soft)]">
                                We do not use this page as a substitute for legal
                                review. Before launch, the final policy should be
                                reviewed against the exact production data flows,
                                vendors, retention windows, and jurisdictions.
                            </p>
                        </div>
                    </div>
                </section>

                <section className="relative py-16 bg-[var(--ink)] text-[var(--paper)]">
                    <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
                        <p className="reveal font-display text-4xl lg:text-6xl leading-tight">
                            Privacy questions go to{" "}
                            <a className="text-[var(--hot)]" href={`mailto:${contactEmail}`}>
                                {contactEmail}
                            </a>
                            .
                        </p>
                    </div>
                </section>

                <WaitlistCTA />
            </main>
            <Footer />
        </div>
    );
}
